/**
 * GRN composer · section 3 — Received quantities.
 *
 * Faithful to the prototype's step-3 `.section` ("Received quantities" ·
 * "Enter what actually arrived. Mark any rejections…") followed by the
 * `.items-table` and the `.ai-hint` summary strip below it.
 *
 * Columns map 1:1 to the prototype:
 *   Item (description + HSN meta-line, + a red `.flag-mini` "N rejected" badge
 *   when the line is short) · Ordered (fixed `.item-cell-fixed`, right) ·
 *   Received (`.item-input is-mono is-right`) · Accepted (same) ·
 *   Rejected / short (computed `.item-cell-fixed`, rose when > 0) ·
 *   Reason (`.item-input`).
 *
 * Cells render bare `<input>` with focus-reveal exactly like the PO line
 * editor. The `.ai-hint` below summarises rejections + PO fulfilment from
 * live form state — no hardcoded demo copy.
 *
 * @see prototype/purchases/goods-receipt-new.html §"3 · Received quantities"
 */
import type { ChangeEvent } from "react";
import { Sparkles, TriangleAlert } from "lucide-react";
import { GrnSectionCard } from "./grn-section-card.js";
import type { GrnLineRow } from "../lib/form-schema.js";
import { computeGrnTotals, milliToDisplay, qtyToMilli, rejectedMilli } from "../lib/grn-math.js";

const cellInput =
  "w-full rounded bg-transparent px-1 py-1 text-sm text-ink-2 " +
  "focus:bg-raised focus:outline-none focus:ring-1 focus:ring-line";
const cellInputMono = "font-mono text-right";

interface GrnReceivedLinesProps {
  rows: GrnLineRow[];
  poNumber: string | null;
  vendorName: string | null;
  disabled?: boolean | undefined;
  error?: string | undefined;
  onRowsChange: (rows: GrnLineRow[]) => void;
}

export function GrnReceivedLines({
  rows,
  poNumber,
  vendorName,
  disabled,
  error,
  onRowsChange,
}: GrnReceivedLinesProps) {
  const updateRow = (id: string, patch: Partial<GrnLineRow>) => {
    onRowsChange(
      rows.map((row) => (row.purchaseOrderLineId === id ? { ...row, ...patch } : row)),
    );
  };

  const totals = computeGrnTotals(rows);
  const hasRows = rows.length > 0;

  return (
    <GrnSectionCard
      step="3"
      title="Received quantities"
      description="Enter what actually arrived. Mark any rejections and add a reason — rejected quantities reduce the accepted count and are noted on the draft matching document."
      bodyless
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Item
              </th>
              <th scope="col" className="w-[96px] px-2 py-2 text-right font-medium">
                Ordered
              </th>
              <th scope="col" className="w-[110px] px-2 py-2 text-right font-medium">
                Received
              </th>
              <th scope="col" className="w-[110px] px-2 py-2 text-right font-medium">
                Accepted
              </th>
              <th scope="col" className="w-[110px] px-2 py-2 text-right font-medium">
                Rejected / short
              </th>
              <th scope="col" className="w-[28%] px-2 py-2 text-left font-medium">
                Reason (if any)
              </th>
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-muted">
                  Pick a purchase order in step 1 to load its lines.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rejected = rejectedMilli(row);
                const short = rejected > 0n;
                return (
                  <tr
                    key={row.purchaseOrderLineId}
                    className={`border-t border-line ${short ? "bg-warn-50" : ""}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <span className="block text-sm font-medium text-ink">
                        {row.description}
                      </span>
                      {row.hsn ? (
                        <span className="mt-0.5 block font-mono text-xs text-muted">
                          HSN {row.hsn}
                        </span>
                      ) : null}
                      {short ? (
                        <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-1.5 py-0.5 text-xs font-semibold text-rose-600">
                          <TriangleAlert className="h-2.5 w-2.5" strokeWidth={2.6} />
                          {milliToDisplay(rejected)} rejected
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 text-right align-top">
                      <span className="block font-mono text-sm text-ink-2">
                        {milliToDisplay(qtyToMilli(row.ordered))}
                      </span>
                    </td>
                    <td className="px-1 py-2 align-top">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Received qty ${row.description}`}
                        value={row.received}
                        disabled={disabled}
                        onChange={onText(row.purchaseOrderLineId, "received", updateRow)}
                        className={`${cellInput} ${cellInputMono}`}
                      />
                    </td>
                    <td className="px-1 py-2 align-top">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Accepted qty ${row.description}`}
                        value={row.accepted}
                        disabled={disabled}
                        onChange={onText(row.purchaseOrderLineId, "accepted", updateRow)}
                        className={`${cellInput} ${cellInputMono}`}
                      />
                    </td>
                    <td className="px-2 py-2 text-right align-top">
                      <span
                        className={`block font-mono text-sm ${short ? "font-semibold text-rose-600" : "text-muted"}`}
                      >
                        {milliToDisplay(rejected)}
                      </span>
                    </td>
                    <td className="px-1 py-2 align-top">
                      <input
                        type="text"
                        aria-label={`Rejection reason ${row.description}`}
                        value={row.rejectReason}
                        disabled={disabled}
                        onChange={onText(row.purchaseOrderLineId, "rejectReason", updateRow)}
                        placeholder="—"
                        className={`min-w-0 ${cellInput}`}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {error ? (
        <p className="border-t border-line px-[18px] py-3 text-sm font-semibold text-bad">
          {error}
        </p>
      ) : null}

      {/* .ai-hint — live rejection + fulfilment summary */}
      {hasRows ? (
        <div className="border-t border-line px-[18px] py-4">
          <div className="flex items-start gap-2.5 rounded-sm border border-sky-100 border-l-[3px] border-l-sky-600 bg-sky-50 px-3 py-2.5 text-xs leading-relaxed text-ink-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-700" aria-hidden />
            <p className="min-w-0 flex-1">
              {totals.rejectedMilli > 0n ? (
                <>
                  <strong className="font-semibold text-ink">
                    {milliToDisplay(totals.rejectedMilli)} units rejected / short.
                  </strong>{" "}
                </>
              ) : (
                <>
                  <strong className="font-semibold text-ink">Full quantity accepted.</strong>{" "}
                </>
              )}
              {totals.acceptedLineCount} of {totals.totalLineCount} line
              {totals.totalLineCount === 1 ? "" : "s"} accepted. Posting this receipt will set{" "}
              {poNumber ?? "the purchase order"} to{" "}
              <span className="font-mono text-ink">
                {totals.poFulfilment === "received" ? "fully received" : "partially received"}
              </span>{" "}
              and draft a matching document{vendorName ? ` for ${vendorName}` : ""}.
            </p>
          </div>
        </div>
      ) : null}
    </GrnSectionCard>
  );
}

function onText(
  id: string,
  field: keyof GrnLineRow,
  updateRow: (id: string, patch: Partial<GrnLineRow>) => void,
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    updateRow(id, { [field]: event.target.value } as Partial<GrnLineRow>);
  };
}
